import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, ArrowLeftRight, RefreshCw, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/TradeAuthContext";
import axios from "axios";
import { toast } from "sonner";

const CurrencyConverter = () => {
  const { API, authHeader } = useAuth();
  const [rates, setRates] = useState({
    rmb_to_usd: 0.14,
    usd_to_etb: 128,
  });
  const [amount, setAmount] = useState(1000);
  const [from, setFrom] = useState("RMB");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    // Load live rates on mount
    axios.get(`${API}/trade/exchange-rates`, { headers: authHeader() })
      .then(res => {
        setRates({
          rmb_to_usd: res.data.rmb_to_usd,
          usd_to_etb: res.data.usd_to_etb,
        });
        setLastUpdated(res.data.updated_at);
      })
      .catch(() => {});
  }, []);

  const refreshRates = async () => {
    setRefreshing(true);
    try {
      const res = await axios.post(`${API}/trade/exchange-rates/refresh`, {}, { headers: authHeader() });
      setRates({
        rmb_to_usd: res.data.rmb_to_usd,
        usd_to_etb: res.data.usd_to_etb,
      });
      setLastUpdated(res.data.updated_at);
      toast.success("Rates updated from market");
    } catch (e) {
      toast.error("Failed to fetch rates");
    } finally {
      setRefreshing(false);
    }
  };

  const currencies = ["RMB", "USD", "ETB"];

  const convert = (amount, from) => {
    const amountNum = parseFloat(amount) || 0;
    let usd;
    if (from === "RMB") usd = amountNum * rates.rmb_to_usd;
    else if (from === "USD") usd = amountNum;
    else if (from === "ETB") usd = amountNum / rates.usd_to_etb;
    
    return {
      RMB: usd / rates.rmb_to_usd,
      USD: usd,
      ETB: usd * rates.usd_to_etb,
    };
  };

  const converted = convert(amount, from);

  const flags = { RMB: "🇨🇳", USD: "🇺🇸", ETB: "🇪🇹" };
  const symbols = { RMB: "¥", USD: "$", ETB: "Br" };
  const fullNames = { RMB: "Chinese Yuan", USD: "US Dollar", ETB: "Ethiopian Birr" };

  return (
    <div className="trade-section pt-32 lg:pt-20 pb-20 min-h-screen">
      <div className="lg:ml-64 px-6 md:px-12 py-10">
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-10"
        >
          <p className="text-xs uppercase tracking-[0.3em] trade-gold mb-3">Converter</p>
          <h1 className="text-4xl md:text-5xl font-serif font-bold">Currency Converter</h1>
          <p className="text-white/50 mt-2">RMB · USD · ETB — all your trade currencies</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          
          {/* Main Converter */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="lg:col-span-3 trade-card rounded-sm p-8"
          >
            <h3 className="text-sm uppercase tracking-widest trade-gold mb-6">Convert</h3>

            <div className="space-y-6">
              <div>
                <Label className="text-xs text-white/60 mb-2 block">Amount</Label>
                <div className="flex gap-3">
                  <Input
                    type="number"
                    value={amount}
                    onChange={e => setAmount(e.target.value)}
                    className="trade-input h-14 rounded-sm text-xl font-mono"
                  />
                  <select
                    value={from}
                    onChange={e => setFrom(e.target.value)}
                    className="trade-input px-4 h-14 rounded-sm text-sm uppercase tracking-wider w-32"
                  >
                    {currencies.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="pt-6 border-t border-white/5 space-y-4">
                <p className="text-xs uppercase tracking-widest text-white/40">Results</p>
                {currencies.filter(c => c !== from).map((c, i) => (
                  <motion.div
                    key={c}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.1 }}
                    className="flex items-center justify-between p-5 bg-[#0a1628] rounded-sm border border-white/5"
                  >
                    <div className="flex items-center gap-4">
                      <span className="text-3xl">{flags[c]}</span>
                      <div>
                        <p className="font-serif text-2xl font-bold">
                          {symbols[c]}{converted[c].toLocaleString(undefined, {maximumFractionDigits: 2})}
                        </p>
                        <p className="text-xs text-white/50">{fullNames[c]}</p>
                      </div>
                    </div>
                    <span className="text-xs uppercase tracking-widest trade-gold">{c}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Exchange Rates */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="lg:col-span-2 space-y-5"
          >
            <div className="trade-card rounded-sm p-6">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-sm uppercase tracking-widest trade-gold">Live Rates</h3>
                <button 
                  onClick={refreshRates}
                  disabled={refreshing}
                  className="text-white/40 hover:trade-gold transition-colors"
                  title="Refresh from market"
                  data-testid="refresh-rates-btn"
                >
                  {refreshing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                </button>
              </div>
              
              {lastUpdated && (
                <p className="text-[10px] text-white/40 mb-4">
                  Updated {new Date(lastUpdated).toLocaleString()}
                </p>
              )}
              
              <div className="space-y-4">
                <div>
                  <Label className="text-xs text-white/60">1 RMB = ? USD</Label>
                  <Input
                    type="number"
                    step="0.0001"
                    value={rates.rmb_to_usd}
                    onChange={e => setRates({...rates, rmb_to_usd: parseFloat(e.target.value) || 0})}
                    className="trade-input mt-1 h-11 rounded-sm font-mono"
                  />
                </div>
                <div>
                  <Label className="text-xs text-white/60">1 USD = ? ETB</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={rates.usd_to_etb}
                    onChange={e => setRates({...rates, usd_to_etb: parseFloat(e.target.value) || 0})}
                    className="trade-input mt-1 h-11 rounded-sm font-mono"
                  />
                </div>
                <div className="pt-3 border-t border-white/5 space-y-2 text-xs">
                  <div className="flex justify-between">
                    <span className="text-white/50">1 USD</span>
                    <span className="font-mono">¥ {(1 / rates.rmb_to_usd).toFixed(2)} RMB</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/50">1 RMB</span>
                    <span className="font-mono">Br {(rates.rmb_to_usd * rates.usd_to_etb).toFixed(2)} ETB</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/50">1 ETB</span>
                    <span className="font-mono">¥ {(1 / (rates.rmb_to_usd * rates.usd_to_etb)).toFixed(4)} RMB</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="trade-card rounded-sm p-5 bg-gradient-to-br from-[#D4AF37]/10 to-transparent">
              <p className="text-xs text-white/60 leading-relaxed">
                💡 Rates auto-update daily at 6am UTC. Click <RefreshCw size={10} className="inline" /> to pull latest now.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default CurrencyConverter;
